import React from 'react';
import * as htmlToImage from 'html-to-image';
import { isEqual, uniq } from 'lodash';
import { ApLoaderDefault } from '@alixpartners/ui-components';
import { PERMISSIONS, PermissionsContext } from 'contexts/PermissionsContext';
import { QueryStringContext } from 'contexts/QueryStringContext';
import ChangeTypeModal from 'Details/ChangeTypeModal/ChangeTypeModal';
import TopVendorsModal from 'Details/TopVendorsModal';
import MoveToBinModal from 'Details/MoveToBinModal/MoveToBinModal';
import {
	getConfigIndex,
	isConfigItemVisible,
} from 'components/PersonalizeOrder';
import { formatSpendWithDecimalAndSymbol } from 'utils/number';
import { convertToArray, downloadAsFile } from 'Details/utils';
import { InvestigateContext } from '../context';
import { ChartOptions } from './ChartOptions';
import { ListTree } from './ListTree';
import { exportInvestigateTreeData, getCategoryTree } from 'utils/apiRequests';
import { SectionWrapper, LoadingContainer } from './styles';
import { CHART_COLORS } from 'styles/colors';
import ContextualMenu from 'Details/ContextualMenu';
import { OrderedItemWrapper } from 'Pmi/styles';
import { ContextualMenuBtnWrapper } from './ListBox/styles';
import {
	getCategoryParam,
	SPEND_BY_LABELS,
	SPEND_BY_PROPS,
	SPEND_TYPE_LABELS,
} from '../utils';
import { SettingsContext } from 'contexts/SettingsContext';

export class CategorySpendTreeChartContainer extends React.Component {
	state = {
		changeStatusModalData: null,
		moveToBinModalData: null,
		chartMinMax: {},
		isLoading: true,
	};

	async componentDidMount() {
		await this.fetchData();
	}

	componentDidUpdate(prevProps) {
		this.fetchDataIfNeeded(prevProps);
	}

	componentWillUnmount() {
		this.unmounted = true;
	}

	fetchDataIfNeeded(prevProps) {
		if (!isEqual(prevProps.queryParams, this.props.queryParams)) {
			this.fetchData();
		}
	}

	fetchData = async () => {
		if (!this.props.currencySymbol) {
			return;
		}

		const rawChartData = await getCategoryTree(this.props.queryParams);

		const chartData = rawChartData.map(
			({ childs, rootName, tailSpend, totalSpend }) => {
				const items = tailSpend ? [...childs, tailSpend] : childs;
				const sortedItems = items.sort((a, b) => b.totalSpend - a.totalSpend);
				return {
					data: sortedItems.map(
						({ currentLevel, nextLevel, totalSpend: childSpend }) => {
							const sortedSubItems = nextLevel.sort(
								(a, b) => b.spend - a.spend
							);
							return {
								name: currentLevel,
								value: this.getFormattedPrice(childSpend),
								data: sortedSubItems.map(
									({ name, spend, spendSplit: nextLevelSpendSplit }) => ({
										name,
										isTailSpend: currentLevel === 'Tail spend',
										id: `${rootName}/${currentLevel}/${name}`,
										parent: currentLevel,
										values: [
											this.getFormattedPrice(spend || 0),
											...nextLevelSpendSplit.map(nextLevelSpendSplitItem =>
												this.getFormattedPrice(
													nextLevelSpendSplitItem.spend || 0
												)
											),
										],
									})
								),
							};
						}
					),
					total: {
						name: rootName,
						id:
							rootName === 'Indirect'
								? 'CategorySpendTreeIndirect'
								: 'CategorySpendTreeDirect',
						value: this.getFormattedPrice(totalSpend),
					},
				};
			}
		);

		if (this.unmounted) return;

		this.setState({
			chartData,
			rawChartData,
			isLoading: false,
		});
	};

	getFormattedPrice(value) {
		const { currencySymbol, hideDecimals } = this.props;

		return formatSpendWithDecimalAndSymbol(
			value,
			currencySymbol,
			hideDecimals,
			2
		).toUpperCase();
	}

	handleSetTopChartRef = chartRef => {
		this.chartRef = chartRef;
	};

	handleExportToPng = id => {
		const chart = document.getElementById(id);
		const fileName = 'chart';

		htmlToImage.toPng(chart).then(dataUrl => {
			downloadAsFile(dataUrl, fileName);
		});
	};

	handleExportData = spendType => {
		const {
			onDownloadStart,
			onDownloadSuccess,
			onDownloadError,
			queryParams,
		} = this.props;

		onDownloadStart();
		exportInvestigateTreeData(queryParams)
			.catch(onDownloadError)
			.finally(onDownloadSuccess);
	};

	handleViewTransactionsClick = item => {
		const { queryParams, showExplorePage, spendType } = this.props;
		const { hierarchy = [], includeTailSpend, getTailSpendOnly } = queryParams;
		const data = {
			includeTailSpend: !includeTailSpend || item.isTailSpend,
			getTailSpendOnly,
		};

		if (item.parent === 'Tail spend') {
			data.hierarchy = [...hierarchy, item.name];
		} else {
			data.hierarchy = [...hierarchy, item.parent, item.name];
		}

		data.isTailSpend = item.parent === 'Tail spend';

		if (spendType != null) {
			data.spendType = SPEND_TYPE_LABELS[spendType].toUpperCase();
		}

		showExplorePage(data, item.name);
	};

	handleChangeStatus = item => {
		const { queryParams, spendBy, spendType } = this.props;
		const { hierarchy = [] } = queryParams;

		const data = { isPmi: true };

		let exploreHierarchy = [];

		if (item.parent === 'Tail spend') {
			exploreHierarchy = [...hierarchy];
		} else {
			exploreHierarchy = [...hierarchy, item.parent];
		}

		if (spendType != null) {
			data.spendType = SPEND_TYPE_LABELS[spendType].toUpperCase();
		}

		if (spendBy === SPEND_BY_PROPS.CATEGORY) {
			data.categoryHierarchy = [...exploreHierarchy, item.name];
		} else {
			data.hierarchy = exploreHierarchy.length > 0 ? exploreHierarchy : null;
			data.selectedDimensionKey = spendBy;
			data.selectedDimensionKeyTitle = SPEND_BY_LABELS[spendBy];
			data.selectedDimensionName = item.name;
		}

		this.setState({
			changeStatusModalData: data,
		});
	};

	showTopVendorsModal = item => {
		const { queryParams, spendType } = this.props;
		const { hierarchy = [], includeTailSpend, getTailSpendOnly } = queryParams;
		const data = {
			includeTailSpend: !includeTailSpend || item.isTailSpend,
			getTailSpendOnly,
			hierarchy: [...hierarchy, item.parent, item.name],
			name: item.name,
		};

		if (spendType != null) {
			data.spendType = SPEND_TYPE_LABELS[spendType].toUpperCase();
		}

		this.setState({
			topVendorsModalData: data,
		});
	};

	hideTopVendorsModal = () => {
		this.setState({
			topVendorsModalData: null,
		});
	};

	handleMoveToBin = (item, parentName) => {
		const {
			indirectTaxonomy,
			directTaxonomy,
			queryParams,
			selectedItem,
			spendBy,
			spendType,
		} = this.props;
		const { hierarchy = [] } = queryParams;

		const dimensionParam = getCategoryParam({
			spendType,
			indirectTaxonomy,
			directTaxonomy,
		});

		const data = {
			isDimensions: true,
		};

		if (spendBy === SPEND_BY_PROPS.CATEGORY) {
			data.categoryHierarchy = [...hierarchy].filter(
				item => item !== 'Tail spend'
			);
			if (data.categoryHierarchy.length < 3) {
				data.categoryHierarchy.push(item.parent);
			}
			if (data.categoryHierarchy.length < 4) {
				data.categoryHierarchy.push(item.name);
			}
		} else {
			if (hierarchy.length || selectedItem) {
				data.extraFilters = {
					[spendBy]: [selectedItem || convertToArray(hierarchy)[0]],
				};
			}
			data.categoryHierarchy = [item.parent, item.name];
		}
		data.requestDimensions = Array(
			Math.min(data.categoryHierarchy.length, 4)
		).fill(dimensionParam);

		if (spendType != null) {
			data.spendType = SPEND_TYPE_LABELS[spendType].toUpperCase();
		} else {
			data.spendType = parentName.toUpperCase();
		}

		this.setState({
			moveToBinModalData: data,
		});
	};

	renderContextualMenu = (item, index, parentName) => {
		const { permissions } = this.props;
		const { menuOpenItemId } = this.state;
		const hasWritePermissions =
			permissions && permissions.includes(PERMISSIONS.TransactionsWrite);
		const isOpen = menuOpenItemId && menuOpenItemId === item.id;

		return (
			<ContextualMenuBtnWrapper open={isOpen}>
				<ContextualMenu
					onViewTransactions={() =>
						this.handleViewTransactionsClick(item, index, parentName)
					}
					hasWritePermissions={hasWritePermissions}
					onChangeStatus={() => this.handleChangeStatus(item, parentName)}
					onShowTopVendors={() => {
						this.showTopVendorsModal(item, parentName);
					}}
					toggleMenu={isOpen =>
						this.setState({ menuOpenItemId: isOpen ? item.id : null })
					}
					moveToBin={() => this.handleMoveToBin(item, parentName)}
					isOpen={isOpen}
				/>
			</ContextualMenuBtnWrapper>
		);
	};

	renderCardHeading = value => {
		const { cardHeadings } = this.props;
		const { rawChartData } = this.state;
		if (rawChartData.length > 0) {
			if (value === 'Indirect') {
				return cardHeadings[4].name;
			} else if (value === 'Direct') {
				return cardHeadings[3].name;
			} else {
				return 'Category Spend Tree';
			}
		}
	};

	render() {
		const { configItems, queryParams, refreshContent } = this.props;
		const {
			changeStatusModalData,
			chartData,
			isLoading,
			topVendorsModalData,
			moveToBinModalData,
		} = this.state;

		if (isLoading) {
			return (
				<SectionWrapper>
					<LoadingContainer>
						<ApLoaderDefault />
					</LoadingContainer>
				</SectionWrapper>
			);
		}

		return (
			<React.Fragment>
				{chartData.map((data, index) => {
					const componentTitle = data.total.name;
					const id = data.total.id;

					return (
						isConfigItemVisible(configItems, id) && (
							<OrderedItemWrapper
								id={id}
								order={getConfigIndex(configItems, id)}
								key={`${data.categoryName}-${index}`}
							>
								<SectionWrapper key={data.total.name}>
									<ChartOptions
										onPngExportClick={this.handleExportToPng}
										onExportData={() => this.handleExportData(componentTitle)}
										label={this.renderCardHeading(componentTitle)}
										exportId={data.total.id}
									/>
									<ListTree
										{...data}
										splitItems={uniq([
											...(queryParams.pivotingFilters || ['Others']),
										])}
										colors={CHART_COLORS}
										renderContextualMenu={this.renderContextualMenu}
									/>
									{topVendorsModalData && (
										<TopVendorsModal
											isPmi
											onClose={this.hideTopVendorsModal}
											{...topVendorsModalData}
											onSubmit={refreshContent}
										/>
									)}
									{changeStatusModalData && (
										<ChangeTypeModal
											isOpen={changeStatusModalData}
											isHarmonized
											{...changeStatusModalData}
											onClose={() => {
												this.setState({ changeStatusModalData: null });
											}}
											isPmi
											onSubmit={refreshContent}
										/>
									)}
									{moveToBinModalData && (
										<MoveToBinModal
											isOpen={moveToBinModalData}
											isHarmonized
											{...moveToBinModalData}
											onClose={() => {
												this.setState({ moveToBinModalData: null });
											}}
											onSubmit={refreshContent}
											isPmi
										/>
									)}
								</SectionWrapper>
							</OrderedItemWrapper>
						)
					);
				})}
			</React.Fragment>
		);
	}
}

export default props => (
	<PermissionsContext.Consumer>
		{({ permissions }) => (
			<InvestigateContext.Consumer>
				{({ refreshContent }) => (
					<QueryStringContext.Consumer>
						{({
							indirectTaxonomy,
							directTaxonomy,
							selectedItem,
							spendBy,
							spendType,
						}) => (
							<SettingsContext.Consumer>
								{({ hideDecimals }) => (
									<CategorySpendTreeChartContainer
										{...props}
										permissions={permissions}
										refreshContent={refreshContent}
										selectedItem={selectedItem}
										spendBy={spendBy}
										spendType={Number(spendType) || undefined}
										indirectTaxonomy={indirectTaxonomy}
										directTaxonomy={directTaxonomy}
										hideDecimals={hideDecimals}
									/>
								)}
							</SettingsContext.Consumer>
						)}
					</QueryStringContext.Consumer>
				)}
			</InvestigateContext.Consumer>
		)}
	</PermissionsContext.Consumer>
);
